import React from "react";
import { motion } from "framer-motion";
import { Linkedin, Mail } from "lucide-react";

type SocialLink = {
  icon: typeof Linkedin;
  url: string;
  label: string;
};

type TeamMember = {
  name: string;
  role: string;
  idLabel: string;
  image: string;
  badges?: string[]; // Array of badge image paths
  socials: SocialLink[];
  calLink?: string; // Cal.com booking link, e.g. "hola-world/consultation-session" — clicking the card opens the booking modal
};

const team: TeamMember[] = [
  {
    name: "Jeswin Antony Jose",
    role: "Qualified Education Agent Counsellor",
    idLabel: "QEAC #14808",
    image: "/team/jeswin-antony-jose.jpg",
    badges: ["/assets/team/badges/qeac-badge.png"],
    calLink: "hola-world/consultation-session",
    socials: [
      {
        icon: Linkedin,
        url: "https://www.linkedin.com/in/jeswin-antony-jose?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
        label: "LinkedIn",
      },
      {
        icon: Mail,
        url: "mailto:jeswin@holaworld.in",
        label: "Email",
      },
    ],
  },
  {
    name: "Jiss Maria Thomas",
    role: "Qualified Education Agent Counsellor",
    idLabel: "QEAC #10625",
    image: "/team/jiss-maria-thomas.jpg",
    badges: ["/assets/team/badges/qeac-badge.png"],
    socials: [
      {
        icon: Linkedin,
        url: "https://www.linkedin.com/in/jissmariathomas?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
        label: "LinkedIn",
      },
      {
        icon: Mail,
        url: "mailto:jiss@holaworld.in",
        label: "Email",
      },
    ],
  },
];

export default function MeetOurTeam() {
  return (
    <section className="py-24 bg-[#EEF5FF] mx-auto w-full px-4">
      <div className="container px-4 mx-auto max-w-6xl">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
            Meet Our <span className="text-sky-600">Team</span>
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto italic">
            Trusted experts guiding your education and migration journey.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className="group relative h-[420px] rounded-3xl overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-500 flex flex-col"
              style={{ minWidth: 0, width: '100%' }}
              {...(member.calLink
                ? {
                    role: "button",
                    tabIndex: 0,
                    "data-cal-namespace": "consultation-session",
                    "data-cal-link": member.calLink,
                    "data-cal-config": '{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}',
                  }
                : {})}
            >
              <img
                src={member.image}
                alt={member.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
              />

              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

              <div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="text-white text-xl font-black tracking-tight mb-1">{member.name}</h3>
                <p className="text-white/70 text-sm font-medium mb-2 uppercase tracking-wider">{member.role}</p>
                <p className="text-white/90 text-xs font-semibold mb-6 tracking-wider">{member.idLabel}</p>

                <div className="flex items-end justify-between gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                  <div className="flex gap-4">
                    {member.socials.map((social, idx) => {
                      const Icon = social.icon;
                      return (
                        <a
                          key={idx}
                          href={social.url}
                          target={social.url.startsWith('mailto:') ? undefined : "_blank"}
                          rel={social.url.startsWith('mailto:') ? undefined : "noopener noreferrer"}
                          title={social.label}
                          onClick={(event) => event.stopPropagation()}
                          className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-sky-600 hover:text-white transition-colors p-0"
                        >
                          <Icon className="w-4 h-4" />
                        </a>
                      );
                    })}
                  </div>

                  {member.badges && member.badges.length > 0 && (
                    <div className="flex gap-2">
                      {member.badges.map((badge, idx) => (
                        <img
                          key={idx}
                          src={badge}
                          alt={`${member.name} badge`}
                          className="h-10 w-10 rounded-lg shadow-lg hover:scale-110 transition-transform"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
